import { useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Divider, Form, Input, message, Modal, notification, Select, Spin, Table } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import axios, { isAxiosError } from 'axios';

import '@/shared/api/core/axios-instance';
import { cycloneApiClient } from '@/shared/api/core/axios-instance';
import type { DeploymentLookupByRunIdItem } from '@/shared/api/deploy';
import { getDeploymentsByRunIds } from '@/shared/api/deploy';
import { useDebounce, useDescriptionValidation } from '@/shared/hooks';
import {
  EMPTY_MESSAGES,
  MODAL_WIDTHS,
  NOTIFICATION_CONFIG,
  NOTIFICATION_MESSAGES,
  PLACEHOLDER_MESSAGES,
  ROUTES,
  VALIDATION_MESSAGES,
  VALIDATION_RULES,
} from '@/shared/lib/constants';
import { formatDateTime } from '@/shared/lib/format/dateTime';
import { highlightText } from '@/shared/lib/text';
import type { DatasetWithTitle, FeatureSetWithTitle, ParameterSetWithTitle } from '@/shared/types';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { Button } from '@/shared/ui/Button';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { ExperimentTagInput } from '@/shared/ui/ExperimentTagInput';
import { Icon } from '@/shared/ui/Icon';
import type { AssetSearchResultData } from '@/shared/ui/InfiniteAssetSelect';
import { InfiniteAssetSelect } from '@/shared/ui/InfiniteAssetSelect';
import { MetricsSummary } from '@/shared/ui/MetricsSummary';
import { type RunStatus, RunStatusTag } from '@/shared/ui/RunStatusTag';
import { SearchInput } from '@/shared/ui/SearchInput';
import { TableEmpty } from '@/shared/ui/TableEmpty';
import { Tooltip } from '@/shared/ui/Tooltip';

import { fetchAssetDetail } from '@/entities/asset-search/api/assetDetailApi';
import {
  useCreateExperimentManagement,
  useExperimentManagementList,
  useUpdateExperimentManagement,
} from '@/entities/experiment';
import { useProject, useUpdateProject } from '@/entities/project';
import type { RunListItem } from '@/entities/run';
import { useRunManagementList } from '@/entities/run';

import { ExperimentComparisonModal } from '@/widgets/experiments/ExperimentComparisonModal/ExperimentComparisonModal';
import {
  COMPUTING_RESOURCE_OPTIONS,
  COMPUTING_RESOURCE_TOOLTIP,
} from '@/widgets/experiments/ResourceTierSelector/resourceTierConfig';
import { RunComparisonModal } from '@/widgets/experiments/RunComparisonModal/RunComparisonModal';

import { MAX_COMPARISON_COUNT, MIN_COMPARISON_COUNT } from './constants';
import styles from './ExperimentContents.module.css';

type ExperimentContentsProps = {
  selectedProjectId?: string;
  selectedExperimentId?: string;
};

export const ExperimentContents = ({ selectedProjectId, selectedExperimentId }: ExperimentContentsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('size')) || 10;
  const searchQuery = searchParams.get('search') || '';
  const sortBy = searchParams.get('sortBy') as 'created_at' | 'updated_at' | 'name' | null;
  const sortOrder = searchParams.get('sortOrder') as 'asc' | 'desc' | null;

  const prevProjectIdRef = useRef(selectedProjectId);
  const prevExperimentIdRef = useRef(selectedExperimentId);
  const isInitialMount = useRef(true);
  const isChangingSizeRef = useRef(false);

  const [selectedExperimentIds, setSelectedExperimentIds] = useState<number[]>([]);
  const [selectedRunIds, setSelectedRunIds] = useState<number[]>([]);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [isExperimentModalOpen, setIsExperimentModalOpen] = useState(false);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPreparingWorkbench, setIsPreparingWorkbench] = useState(false);
  const [experimentForm] = Form.useForm();
  const [runForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const experimentValidation = useDescriptionValidation({ errorMessage: '실험 생성 실패' });
  const editValidation = useDescriptionValidation({ errorMessage: '수정 실패' });

  const [selectedAssetData, setSelectedAssetData] = useState<{
    dataset?: DatasetWithTitle;
    featureSet?: FeatureSetWithTitle;
    parameterSet?: ParameterSetWithTitle;
  }>({});
  const [computingResource, setComputingResource] = useState<string>('c4-basic');
  const [runTags, setRunTags] = useState<string[]>(['']);
  const [isTagsValid, setIsTagsValid] = useState(true);
  const [deploymentStatusMap, setDeploymentStatusMap] = useState<
    Map<number, { status: RunStatus; deploymentId?: string }>
  >(new Map());
  const [isLoadingDeploymentStatuses, setIsLoadingDeploymentStatuses] = useState(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const projectId = selectedProjectId ? Number(selectedProjectId) : undefined;
  const experimentId = selectedExperimentId ? Number(selectedExperimentId) : undefined;

  const handleAssetSelection = async (
    assetType: 'dataset' | 'feature_set' | 'parameter_set',
    data: AssetSearchResultData
  ) => {
    try {
      const detail = await fetchAssetDetail({ assetId: data.id.toString(), type: assetType });

      setSelectedAssetData((prev) => {
        if (assetType === 'dataset' && detail.dataset) {
          return { ...prev, dataset: { ...detail.dataset, id: data.id, title: data.title, runId: undefined } };
        }
        if (assetType === 'feature_set' && detail.featureSet) {
          return { ...prev, featureSet: { ...detail.featureSet, title: data.title, runId: undefined } };
        }
        if (assetType === 'parameter_set' && detail.parameterSet) {
          return {
            ...prev,
            parameterSet: { ...detail.parameterSet, id: data.id, title: data.title, runId: undefined },
          };
        }
        return prev;
      });
    } catch (error) {
      console.error(`Failed to fetch ${assetType} detail:`, error);
      notification.error({
        message: NOTIFICATION_MESSAGES.ASSET_DETAIL_FETCH_ERROR,
        description: NOTIFICATION_MESSAGES.ASSET_DETAIL_FETCH_ERROR_DESC,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
        duration: NOTIFICATION_CONFIG.DURATION.ERROR,
      });
    }
  };

  useEffect(() => {
    const projectChanged = prevProjectIdRef.current !== selectedProjectId;
    const experimentChanged = prevExperimentIdRef.current !== selectedExperimentId;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      const currentSize = searchParams.get('size') || '10';
      const currentPage = searchParams.get('page');

      if (!currentPage) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('size', currentSize);
        newParams.set('page', '1');
        setSearchParams(newParams, { replace: true });
      }
    } else if (projectChanged || experimentChanged) {
      setSelectedExperimentIds([]);
      setSelectedRunIds([]);

      const currentSize = searchParams.get('size') || '10';
      const newParams = new URLSearchParams(searchParams);
      newParams.set('size', currentSize);
      newParams.set('page', '1');
      setSearchParams(newParams, { replace: true });
    }

    prevProjectIdRef.current = selectedProjectId;
    prevExperimentIdRef.current = selectedExperimentId;
  }, [selectedProjectId, selectedExperimentId, searchParams, setSearchParams]);

  const { data: projectData } = useProject(projectId!, !!projectId);

  const experimentsQueryParams = useMemo(() => {
    if (experimentId) {
      return {
        projectId: projectId!,
        page: 1,
        size: 100,
      };
    }
    return {
      projectId: projectId!,
      query: debouncedSearchQuery || undefined,
      page: currentPage,
      size: pageSize,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
    };
  }, [projectId, experimentId, debouncedSearchQuery, currentPage, pageSize, sortBy, sortOrder]);

  const runsQueryParams = useMemo(() => {
    return {
      experimentId: experimentId!,
      query: debouncedSearchQuery || undefined,
      page: currentPage,
      size: pageSize,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
    };
  }, [experimentId, debouncedSearchQuery, currentPage, pageSize, sortBy, sortOrder]);

  const { data: experimentsResponse } = useExperimentManagementList(experimentsQueryParams, !!projectId);
  const { data: runsResponse } = useRunManagementList(runsQueryParams, !!experimentId);

  const createExperimentMutation = useCreateExperimentManagement();
  const updateProjectMutation = useUpdateProject();
  const updateExperimentMutation = useUpdateExperimentManagement(experimentId || 0);

  const experiments = useMemo(() => experimentsResponse?.experiments || [], [experimentsResponse?.experiments]);
  const runs = useMemo(() => runsResponse?.runs || [], [runsResponse?.runs]);
  const experiment = experimentId ? experiments.find((e) => e.id === experimentId) : undefined;

  const succeededRunIds = useMemo(
    () =>
      runs
        .filter((run) => run.status === 'execution_succeeded')
        .map((run) => run.id)
        .sort((a, b) => a - b)
        .join(','),
    [runs]
  );

  useEffect(() => {
    if (succeededRunIds) {
      setIsLoadingDeploymentStatuses(true);
    } else {
      setIsLoadingDeploymentStatuses(false);
    }
  }, [succeededRunIds]);

  useEffect(() => {
    const fetchDeploymentStatuses = async () => {
      if (!succeededRunIds) {
        setDeploymentStatusMap(new Map());
        setIsLoadingDeploymentStatuses(false);
        return;
      }

      setIsLoadingDeploymentStatuses(true);
      const runIdArray = succeededRunIds.split(',').map((id) => parseInt(id, 10));

      try {
        const response = await getDeploymentsByRunIds({
          run_ids: runIdArray.map((id) => id.toString()),
          environment: 'dev',
          include_deleted: false,
        });

        const statusMap = new Map<number, { status: RunStatus; deploymentId?: string }>();
        response.results.forEach((result: DeploymentLookupByRunIdItem) => {
          if (result.found && result.deployment) {
            const runId = parseInt(result.run_id, 10);
            const deploymentStatus = result.deployment.status;

            let mappedStatus: RunStatus;

            if (deploymentStatus === 'deployment_succeeded') {
              mappedStatus = 'serving_inactive';
            } else if (deploymentStatus === 'serving_failed') {
              mappedStatus = 'deployment_failed';
            } else if (deploymentStatus === 'serving_in_progress') {
              mappedStatus = 'deployment_in_progress';
            } else {
              mappedStatus = deploymentStatus as RunStatus;
            }

            statusMap.set(runId, {
              status: mappedStatus,
              deploymentId: result.deployment.deployment_id,
            });
          }
        });

        setDeploymentStatusMap(statusMap);
      } catch (error) {
        console.error('[ExperimentContents] Failed to fetch deployment statuses:', error);
        setDeploymentStatusMap(new Map());
      } finally {
        setIsLoadingDeploymentStatuses(false);
      }
    };

    if (succeededRunIds) {
      fetchDeploymentStatuses();
    } else {
      setIsLoadingDeploymentStatuses(false);
    }
  }, [succeededRunIds]);

  const selectedExperiments = useMemo(() => {
    return experiments.filter((exp) => selectedExperimentIds.includes(exp.id));
  }, [experiments, selectedExperimentIds]);

  const selectedRuns = useMemo(() => {
    return runs.filter((run) => selectedRunIds.includes(run.id));
  }, [runs, selectedRunIds]);

  const experimentColumns = [
    {
      title: '실험명',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: true,
      sortOrder: (sortBy === 'name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null) as SortOrder,
      render: (text: string, record: { id: number }) => (
        <a
          className={styles.experimentName}
          onClick={() => navigate(`${ROUTES.EXPERIMENTS}/${projectId}/${record.id}`)}>
          <EllipsisText text={text}>
            {highlightText(text, debouncedSearchQuery, { className: 'highlight' })}
          </EllipsisText>
        </a>
      ),
    },
    {
      title: '설명',
      dataIndex: 'description',
      key: 'description',
      render: (text: string | null) => (
        <AssetDescription
          description={text || undefined}
          mode="tooltip"
          highlightQuery={debouncedSearchQuery}
          highlightClassName="highlight"
        />
      ),
    },
    {
      title: '소유자',
      dataIndex: 'creator',
      key: 'creator',
      width: 200,
      ellipsis: true,
      render: (text: string | null) => <EllipsisText text={text || '-'} />,
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: true,
      sortOrder: (sortBy === 'created_at' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null) as SortOrder,
      render: (text: string) => formatDateTime(text),
    },
    {
      title: '수정일',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      sorter: true,
      sortOrder: (sortBy === 'updated_at' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null) as SortOrder,
      render: (text: string) => formatDateTime(text),
    },
    {
      title: '런 수',
      dataIndex: 'runCount',
      key: 'runCount',
      width: 120,
    },
  ];

  const runColumns = [
    {
      title: '런명',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: true,
      sortOrder: (sortBy === 'name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null) as SortOrder,
      render: (text: string, record: { id: number }) => (
        <a
          className={styles.runName}
          onClick={() => navigate(`/experiments/${projectId}/${experimentId}/${record.id}?assetType=run`)}>
          <EllipsisText text={text}>
            {highlightText(text, debouncedSearchQuery, { className: 'highlight' })}
          </EllipsisText>
        </a>
      ),
    },
    {
      title: '설명',
      dataIndex: 'description',
      key: 'description',
      render: (text: string | null) => (
        <AssetDescription
          description={text || undefined}
          mode="tooltip"
          highlightQuery={debouncedSearchQuery}
          highlightClassName="highlight"
        />
      ),
    },
    {
      title: '소유자',
      dataIndex: 'creator',
      key: 'creator',
      width: 200,
      ellipsis: true,
      render: (text: string | null) => <EllipsisText text={text || '-'} />,
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: true,
      sortOrder: (sortBy === 'created_at' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null) as SortOrder,
      render: (text: string) => formatDateTime(text),
    },
    {
      title: '메트릭',
      key: 'metrics',
      width: 180,
      render: (_: unknown, record: RunListItem) => <MetricsSummary metrics={record.metrics} />,
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      align: 'left' as const,
      render: (status: RunStatus, record: RunListItem) => {
        const isSucceeded = status === 'execution_succeeded';
        const deploymentStatus = deploymentStatusMap.get(record.id);

        if (isSucceeded && isLoadingDeploymentStatuses && !deploymentStatus) {
          return (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '24px',
                minHeight: '24px',
              }}>
              <Spin indicator={<LoadingOutlined spin style={{ fontSize: 14 }} />} />
            </div>
          );
        }

        if (deploymentStatus) {
          return <RunStatusTag status={deploymentStatus.status} />;
        }
        return <RunStatusTag status={status} />;
      },
    },
  ];

  const currentSelectedCount = experimentId ? selectedRunIds.length : selectedExperimentIds.length;
  const canCompare = currentSelectedCount >= MIN_COMPARISON_COUNT && currentSelectedCount <= MAX_COMPARISON_COUNT;

  const handleCompare = () => {
    if (!canCompare) return;
    setIsComparisonModalOpen(true);
  };

  const rowSelection = {
    selectedRowKeys: experimentId ? selectedRunIds : selectedExperimentIds,
    columnWidth: 60,
    onChange: (selectedRowKeys: React.Key[]) => {
      if (selectedRowKeys.length > MAX_COMPARISON_COUNT) {
        const totalCount = experimentId ? runs.length : experiments.length;
        const currentCount = experimentId ? selectedRunIds.length : selectedExperimentIds.length;
        const addedCount = selectedRowKeys.length - currentCount;

        // 헤더 전체 선택 시도(한 번에 여러 행): 토스트 없이 무시 → onSelectAll에서 전체 해제
        if (selectedRowKeys.length === totalCount && addedCount > 1) {
          return;
        }

        // 개별 선택으로 4개 초과할 때만 경고 (해제·전체 취소 시에는 경고 없음)
        if (addedCount > 0) {
          message.warning(`최대 ${MAX_COMPARISON_COUNT}개까지 비교 가능합니다.`);
        }
        return;
      }
      if (experimentId) {
        setSelectedRunIds(selectedRowKeys as number[]);
      } else {
        setSelectedExperimentIds(selectedRowKeys as number[]);
      }
    },
    onSelectAll: () => {
      if (experimentId) {
        setSelectedRunIds([]);
      } else {
        setSelectedExperimentIds([]);
      }
    },
    columnTitle: (originalElement: React.ReactNode) => {
      return (
        <div
          className={styles.checkboxColumn}
          style={{
            visibility: currentSelectedCount > 0 ? 'visible' : 'hidden',
          }}>
          {originalElement}
        </div>
      );
    },
  };

  const handleCreateExperiment = async () => {
    if (!projectId) return;

    try {
      const values = await experimentForm.validateFields();

      if (!experimentValidation.validateBeforeSubmit(values.description)) {
        return;
      }

      try {
        const newExperiment = await createExperimentMutation.mutateAsync({
          experimentName: values.name,
          projectId: projectId,
          description: values.description,
        });
        setIsExperimentModalOpen(false);
        experimentValidation.resetError();
        experimentForm.resetFields();
        const size = searchParams.get('size');
        const url = size
          ? `${ROUTES.EXPERIMENTS}/${projectId}/${newExperiment.id}?size=${size}`
          : `${ROUTES.EXPERIMENTS}/${projectId}/${newExperiment.id}`;
        navigate(url);
      } catch (error: unknown) {
        message.error('실험 생성 실패');
        if (isAxiosError(error) && error.response?.data?.message) {
          const errorMessage = error.response.data.message;
          if (errorMessage.includes('already exists') || errorMessage.includes('이미 존재')) {
            experimentForm.setFields([
              {
                name: 'name',
                errors: [VALIDATION_MESSAGES.NAME_ALREADY_EXISTS],
              },
            ]);
          }
        }
      }
    } catch {
      // Validation error already shown by validateBeforeSubmit
    }
  };

  const handleCreateRun = async () => {
    if (!experimentId || !projectData || !experiment) return;

    try {
      const values = await runForm.validateFields();

      if (!isTagsValid) {
        message.error('런 생성 실패');
        return;
      }

      const datasetSnapshotId = selectedAssetData.dataset?.datasetSnapshotId ?? null;
      const featureSetSnapshotId = selectedAssetData.featureSet?.featureSetSnapshotId ?? null;
      const parameterSetId = selectedAssetData.parameterSet?.id ?? null;
      const filteredTags = runTags.filter((tag) => tag.trim() !== '');

      const response = await cycloneApiClient.post(
        '/runs',
        {
          experimentId,
          runName: values.name,
          datasetSnapshotId,
          featureSetSnapshotId,
          parameterSetId,
          tags: filteredTags.length > 0 ? filteredTags : undefined,
        },
        {
          skipGlobalErrorNotification: true,
        }
      );

      if (!response?.data?.id) {
        message.error('런 생성에 실패했습니다. (응답 데이터 없음)');
        return;
      }

      const newRunId = response.data.id;

      message.success('런 생성에 성공했습니다.');
      setIsRunModalOpen(false);
      runForm.resetFields();
      setSelectedAssetData({});
      setComputingResource('c4-basic');
      setRunTags(['']);
      setIsTagsValid(true);
      setIsPreparingWorkbench(true);

      await queryClient.invalidateQueries({ queryKey: ['run-management'] });
      await queryClient.invalidateQueries({ queryKey: ['experiment-management'] });
      await queryClient.invalidateQueries({ queryKey: ['projects-experiments'] });

      const runDetail = await fetchAssetDetail({
        assetId: newRunId,
        type: 'run',
      });

      await new Promise((resolve) => setTimeout(resolve, 500));

      const workbenchData = {
        project: {
          id: projectData.id,
          name: projectData.name,
        },
        experiment: {
          id: experiment.id,
          name: experiment.name,
        },
        run: {
          name: values.name,
          description: values.description || undefined,
          status: runDetail.run?.status,
        },
        selectedAssets: {
          datasetId: selectedAssetData.dataset?.table || runDetail.dataset?.table,
          featureSetId: selectedAssetData.featureSet?.table || runDetail.featureSet?.table,
          parameterSetId: selectedAssetData.parameterSet?.id || runDetail.parameterSet?.id,
        },
        assetDetails: {
          dataset:
            selectedAssetData.dataset ||
            (runDetail.dataset ? { ...runDetail.dataset, title: runDetail.dataset.namespace || '' } : undefined),
          featureSet:
            selectedAssetData.featureSet ||
            (runDetail.featureSet
              ? { ...runDetail.featureSet, title: runDetail.featureSet.namespace || '' }
              : undefined),
          parameterSet:
            selectedAssetData.parameterSet ||
            (runDetail.parameterSet
              ? { ...runDetail.parameterSet, title: `ParameterSet-${runDetail.parameterSet.id}` }
              : undefined),
        },
        tiers: {
          cpu: runDetail.run?.cpuTier || 'c4-basic',
          gpu: runDetail.run?.gpuTier || 'dl-light',
        },
      };

      setIsPreparingWorkbench(false);
      setSelectedAssetData({});
      navigate(`/experiments/workbench/${newRunId}`, {
        state: { workbenchData },
      });
    } catch (error) {
      console.error('Failed to create run:', error);

      if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;
        const errorData = data as { error?: string; message?: string };

        if (status === 409 && errorData.message) {
          message.error('런 생성 실패');
          runForm.setFields([
            {
              name: 'name',
              errors: [VALIDATION_MESSAGES.NAME_ALREADY_EXISTS],
            },
          ]);
        } else if (status === 404 && errorData.message) {
          const errorType = errorData.error;

          if (errorType === 'DATASET_SNAPSHOT_NOT_FOUND') {
            runForm.setFields([
              {
                name: 'dataset',
                errors: [errorData.message],
              },
            ]);
          } else if (errorType === 'FEATURE_SET_SNAPSHOT_NOT_FOUND') {
            runForm.setFields([
              {
                name: 'featureSet',
                errors: [errorData.message],
              },
            ]);
          } else if (errorType === 'PARAMETER_SET_NOT_FOUND') {
            runForm.setFields([
              {
                name: 'parameterSet',
                errors: [errorData.message],
              },
            ]);
          } else {
            message.error('런 생성 실패');
          }
        } else {
          message.error('런 생성 실패');
        }
      } else if (error && typeof error === 'object' && 'errorFields' in error) {
        message.error('런 생성 실패');
      } else {
        message.error('런 생성 실패');
      }

      setIsPreparingWorkbench(false);
    }
  };

  const handleEdit = () => {
    if (experimentId && experiment) {
      editForm.setFieldsValue({
        name: experiment.name,
        description: experiment.description || '',
      });
      setIsEditModalOpen(true);
    } else if (projectId && projectData) {
      editForm.setFieldsValue({
        name: projectData.name,
        description: projectData.description || '',
      });
      setIsEditModalOpen(true);
    }
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();

      if (!editValidation.validateBeforeSubmit(values.description)) {
        return;
      }

      if (experimentId) {
        await updateExperimentMutation.mutateAsync({
          experimentName: values.name,
          description: values.description || null,
        });
      } else if (projectId) {
        await updateProjectMutation.mutateAsync({
          projectId,
          data: {
            name: values.name,
            description: values.description,
          },
        });
      }

      setIsEditModalOpen(false);
      editForm.resetFields();
    } catch {
      // Validation error already shown by validateBeforeSubmit
    }
  };

  if (!projectId) {
    return (
      <div className={styles.emptyState}>
        <Icon name="experiment" width={48} color="#d9d9d9" />
        <p>프로젝트를 선택해 주세요.</p>
      </div>
    );
  }

  return (
    <article className={styles.mainContentArea}>
      <ContentHeader
        title={experiment ? experiment.name : projectData?.name}
        description={experiment ? experiment.description : projectData?.description}
        actions={
          <div className={styles.actionButtons}>
            <Button prefix={<Icon name="edit" size={16} />} onClick={handleEdit} variant="outlined" color="standard">
              수정
            </Button>
            {!experimentId ? (
              <Button prefix={<Icon name="add" size={16} />} onClick={() => setIsExperimentModalOpen(true)}>
                실험 생성
              </Button>
            ) : (
              <Button prefix={<Icon name="add" size={16} />} onClick={() => setIsRunModalOpen(true)}>
                런 생성
              </Button>
            )}
          </div>
        }
        showCopyButton={false}
        className={styles.contentHeader}
      />

      <div className={styles.mainContent}>
        <div className={styles.contentArea}>
          <div className={styles.searchInputWrapper}>
            <SearchInput
              placeholder={experimentId ? PLACEHOLDER_MESSAGES.RUN_SEARCH : PLACEHOLDER_MESSAGES.EXPERIMENT_SEARCH}
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchParams(
                  (prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    if (value) {
                      params.set('search', value);
                    } else {
                      params.delete('search');
                    }
                    return params;
                  },
                  { replace: true }
                );
              }}
            />
            <Button
              prefix={<Icon name="compare" size={16} />}
              onClick={handleCompare}
              disabled={!canCompare}
              variant="outlined"
              color="standard">
              비교
            </Button>
          </div>
          <div className={styles.tableWrapper}>
            <div className={styles.tableContainer}>
              {!experimentId ? (
                <Table
                  dataSource={experiments}
                  columns={experimentColumns}
                  rowKey="id"
                  rowSelection={rowSelection}
                  onChange={(_, __, sorter, { action }) => {
                    if (action !== 'sort') return;

                    if (!Array.isArray(sorter) && sorter.order) {
                      const field = sorter.field as string;
                      const sortByValue =
                        field === 'name' ? 'name' : field === 'createdAt' ? 'created_at' : 'updated_at';
                      const sortOrderValue = sorter.order === 'ascend' ? 'asc' : 'desc';

                      setSearchParams(
                        (prev) => {
                          const params = new URLSearchParams(prev);
                          params.set('sortBy', sortByValue);
                          params.set('sortOrder', sortOrderValue);
                          params.set('page', '1');
                          return params;
                        },
                        { replace: true }
                      );
                    } else if (!Array.isArray(sorter) && !sorter.order) {
                      setSearchParams(
                        (prev) => {
                          const params = new URLSearchParams(prev);
                          params.delete('sortBy');
                          params.delete('sortOrder');
                          params.set('page', '1');
                          return params;
                        },
                        { replace: true }
                      );
                    }
                  }}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: experimentsResponse?.pagination.totalCount,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `(${total}개 중 ${range[0]}-${range[1]})`,
                    locale: { items_per_page: '' },
                    hideOnSinglePage: false,
                    onChange: (page) => {
                      if (isChangingSizeRef.current) {
                        isChangingSizeRef.current = false;
                        return;
                      }
                      setSearchParams(
                        (prev) => {
                          const params = new URLSearchParams(prev);
                          params.set('page', page.toString());
                          return params;
                        },
                        { replace: true }
                      );
                    },
                    onShowSizeChange: (_, size) => {
                      isChangingSizeRef.current = true;
                      setSearchParams(
                        (prev) => {
                          const params = new URLSearchParams(prev);
                          params.set('size', size.toString());
                          params.set('page', '1');
                          return params;
                        },
                        { replace: true }
                      );
                    },
                  }}
                  scroll={{
                    x: experiments && experiments.length > 0 ? 'max-content' : undefined,
                    y: 'max(calc(100vh - 340px), 628px)',
                  }}
                  locale={{
                    emptyText: (
                      <TableEmpty
                        searchQuery={debouncedSearchQuery}
                        description={debouncedSearchQuery ? undefined : EMPTY_MESSAGES.NO_EXPERIMENTS}
                      />
                    ),
                  }}
                  className={styles.table}
                />
              ) : (
                <Table
                  dataSource={runs}
                  columns={runColumns}
                  rowKey="id"
                  rowSelection={rowSelection}
                  onChange={(_, __, sorter, { action }) => {
                    if (action !== 'sort') return;

                    if (!Array.isArray(sorter) && sorter.order) {
                      const field = sorter.field as string;
                      const sortByValue =
                        field === 'name' ? 'name' : field === 'createdAt' ? 'created_at' : 'updated_at';
                      const sortOrderValue = sorter.order === 'ascend' ? 'asc' : 'desc';

                      setSearchParams(
                        (prev) => {
                          const params = new URLSearchParams(prev);
                          params.set('sortBy', sortByValue);
                          params.set('sortOrder', sortOrderValue);
                          params.set('page', '1');
                          return params;
                        },
                        { replace: true }
                      );
                    } else if (!Array.isArray(sorter) && !sorter.order) {
                      setSearchParams(
                        (prev) => {
                          const params = new URLSearchParams(prev);
                          params.delete('sortBy');
                          params.delete('sortOrder');
                          params.set('page', '1');
                          return params;
                        },
                        { replace: true }
                      );
                    }
                  }}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: runsResponse?.pagination.totalCount,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `(${total}개 중 ${range[0]}-${range[1]})`,
                    locale: { items_per_page: '' },
                    hideOnSinglePage: false,
                    onChange: (page) => {
                      if (isChangingSizeRef.current) {
                        isChangingSizeRef.current = false;
                        return;
                      }
                      setSearchParams(
                        (prev) => {
                          const params = new URLSearchParams(prev);
                          params.set('page', page.toString());
                          return params;
                        },
                        { replace: true }
                      );
                    },
                    onShowSizeChange: (_, size) => {
                      isChangingSizeRef.current = true;
                      setSearchParams(
                        (prev) => {
                          const params = new URLSearchParams(prev);
                          params.set('size', size.toString());
                          params.set('page', '1');
                          return params;
                        },
                        { replace: true }
                      );
                    },
                  }}
                  scroll={{
                    x: runs && runs.length > 0 ? 'max-content' : undefined,
                    y: 'max(calc(100vh - 340px), 628px)',
                  }}
                  locale={{
                    emptyText: (
                      <TableEmpty
                        searchQuery={debouncedSearchQuery}
                        description={debouncedSearchQuery ? undefined : EMPTY_MESSAGES.NO_EXPERIMENT_RUNS}
                      />
                    ),
                  }}
                  className={styles.table}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <ExperimentComparisonModal
        open={isComparisonModalOpen && !experimentId}
        experiments={selectedExperiments}
        onClose={() => setIsComparisonModalOpen(false)}
      />

      <RunComparisonModal
        open={isComparisonModalOpen && !!experimentId}
        runs={selectedRuns}
        onClose={() => setIsComparisonModalOpen(false)}
      />

      <Modal
        title="새 실험 생성"
        open={isExperimentModalOpen}
        onOk={handleCreateExperiment}
        onCancel={() => {
          setIsExperimentModalOpen(false);
          experimentValidation.resetError();
          experimentForm.resetFields();
        }}
        okText="생성"
        cancelText="취소"
        confirmLoading={createExperimentMutation.isPending}
        destroyOnHidden
        width={MODAL_WIDTHS.COMPACT_FORM}>
        <Form
          form={experimentForm}
          layout="vertical"
          colon={false}
          initialValues={{ name: '', description: '' }}
          onValuesChange={experimentValidation.handleValuesChange}>
          <Form.Item label="프로젝트" required>
            <Input value={projectData?.name} disabled placeholder="프로젝트를 선택하세요" />
          </Form.Item>
          <Form.Item
            name="name"
            label="실험명"
            rules={[
              { required: true, message: VALIDATION_MESSAGES.EXPERIMENT_NAME_REQUIRED },
              {
                max: VALIDATION_RULES.EXPERIMENT_NAME_MAX_LENGTH,
                message: `실험명은 최대 ${VALIDATION_RULES.EXPERIMENT_NAME_MAX_LENGTH}자까지 입력 가능합니다`,
              },
            ]}
            validateFirst>
            <Input
              placeholder="실험명을 입력하세요"
              className={styles.experimentNameInput}
              maxLength={VALIDATION_RULES.EXPERIMENT_NAME_MAX_LENGTH}
              allowClear
            />
          </Form.Item>
          <Form.Item name="description" label="설명" required>
            <DescriptionTextArea
              placeholder="실험에 대한 설명을 입력하세요"
              showHelper
              minLength={VALIDATION_RULES.DESCRIPTION_MIN_LENGTH}
              maxLength={VALIDATION_RULES.DESCRIPTION_MAX_LENGTH}
              status={experimentValidation.descriptionError ? 'error' : undefined}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="새 런 생성"
        open={isRunModalOpen}
        onOk={handleCreateRun}
        onCancel={() => {
          setIsRunModalOpen(false);
          runForm.resetFields();
          setSelectedAssetData({});
          setComputingResource('c4-basic');
          setRunTags(['']);
          setIsTagsValid(true);
        }}
        okText="생성 후 작업 열기"
        cancelText="취소"
        confirmLoading={isPreparingWorkbench}
        destroyOnHidden
        width={MODAL_WIDTHS.COMPACT_FORM}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}>
        <Form
          form={runForm}
          layout="horizontal"
          colon={false}
          onSubmitCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
          <Form.Item label="프로젝트" required>
            <Select value={projectData?.name} disabled placeholder="프로젝트를 선택하세요" />
          </Form.Item>
          <Form.Item label="실험" required>
            <Select value={experiment?.name} disabled placeholder="실험을 선택하세요" />
          </Form.Item>
          <Form.Item
            name="name"
            label="런명"
            rules={[
              { required: true, message: VALIDATION_MESSAGES.RUN_NAME_REQUIRED },
              {
                max: VALIDATION_RULES.RUN_NAME_MAX_LENGTH,
                message: `런명은 최대 ${VALIDATION_RULES.RUN_NAME_MAX_LENGTH}자까지 입력 가능합니다`,
              },
            ]}
            validateFirst>
            <Input
              placeholder="런명을 입력하세요"
              className={styles.runNameInput}
              maxLength={VALIDATION_RULES.RUN_NAME_MAX_LENGTH}
              allowClear
            />
          </Form.Item>
          <Divider style={{ margin: '16px 0' }} />
          <Form.Item name="dataset" label="데이터셋" initialValue={selectedAssetData.dataset?.id}>
            <InfiniteAssetSelect
              assetType="dataset"
              placeholder="데이터셋을 검색하세요"
              allowClear
              onSelectAsset={(data) => handleAssetSelection('dataset', data)}
              onChange={(value) => {
                if (!value) {
                  setSelectedAssetData((prev) => ({ ...prev, dataset: undefined }));
                  runForm.setFields([
                    {
                      name: 'dataset',
                      errors: [],
                    },
                  ]);
                } else {
                  runForm.setFieldValue('dataset', value);
                }
              }}
            />
          </Form.Item>
          <Form.Item name="featureSet" label="피처셋" initialValue={selectedAssetData.featureSet?.id}>
            <InfiniteAssetSelect
              assetType="feature_set"
              placeholder="피처셋을 검색하세요"
              allowClear
              onSelectAsset={(data) => handleAssetSelection('feature_set', data)}
              onChange={(value) => {
                if (!value) {
                  setSelectedAssetData((prev) => ({ ...prev, featureSet: undefined }));
                  runForm.setFields([
                    {
                      name: 'featureSet',
                      errors: [],
                    },
                  ]);
                } else {
                  runForm.setFieldValue('featureSet', value);
                }
              }}
            />
          </Form.Item>
          <Form.Item name="parameterSet" label="파라미터셋" initialValue={selectedAssetData.parameterSet?.id}>
            <InfiniteAssetSelect
              assetType="run_parameter_set"
              placeholder="파라미터셋을 검색하세요"
              allowClear
              onSelectAsset={(data) => handleAssetSelection('parameter_set', data)}
              onChange={(value) => {
                if (!value) {
                  setSelectedAssetData((prev) => ({ ...prev, parameterSet: undefined }));
                  runForm.setFields([
                    {
                      name: 'parameterSet',
                      errors: [],
                    },
                  ]);
                } else {
                  runForm.setFieldValue('parameterSet', value);
                }
              }}
            />
          </Form.Item>
          <Divider style={{ margin: '16px 0' }} />
          <Form.Item
            label={
              <span className={styles.formLabelWithTooltip}>
                컴퓨팅 리소스
                <Tooltip
                  title={COMPUTING_RESOURCE_TOOLTIP}
                  placement="bottomLeft"
                  showArrow={false}
                  classNames={{ root: styles.resourceTooltip }}>
                  <QuestionCircleOutlined className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
            colon={false}>
            <Select
              value={computingResource}
              onChange={setComputingResource}
              options={COMPUTING_RESOURCE_OPTIONS}
              placeholder="컴퓨팅 리소스를 선택하세요"
            />
          </Form.Item>
          <Divider style={{ margin: '16px 0' }} />
          <div className={styles.tagSection}>
            <div className={styles.tagLabelWrapper}>
              <span className={styles.tagLabel}>태그</span>
              <span className={styles.tagRequired} />
            </div>
            <ExperimentTagInput
              value={runTags}
              onChange={setRunTags}
              scrollable
              showAddButton
              onValidationChange={setIsTagsValid}
              errorText={!isTagsValid ? '이미 존재하는 태그입니다. 다른 태그를 입력해 주세요' : undefined}
            />
          </div>
        </Form>
      </Modal>

      <Modal
        title={experimentId ? '실험 수정' : '프로젝트 수정'}
        open={isEditModalOpen}
        onOk={handleUpdate}
        onCancel={() => {
          setIsEditModalOpen(false);
          editValidation.resetError();
          editForm.resetFields();
        }}
        okText="수정"
        cancelText="취소"
        confirmLoading={updateExperimentMutation.isPending || updateProjectMutation.isPending}
        destroyOnHidden
        width={MODAL_WIDTHS.COMPACT_FORM}>
        <Form form={editForm} layout="vertical" colon={false} onValuesChange={editValidation.handleValuesChange}>
          {experimentId && (
            <Form.Item label="프로젝트" required>
              <Input value={projectData?.name} disabled placeholder="프로젝트를 선택하세요" />
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label={experimentId ? '실험명' : '프로젝트명'}
            rules={[
              {
                required: true,
                message: experimentId
                  ? VALIDATION_MESSAGES.EXPERIMENT_NAME_REQUIRED
                  : VALIDATION_MESSAGES.PROJECT_NAME_REQUIRED,
              },
              experimentId
                ? {
                    max: VALIDATION_RULES.EXPERIMENT_NAME_MAX_LENGTH,
                    message: `실험명은 최대 ${VALIDATION_RULES.EXPERIMENT_NAME_MAX_LENGTH}자까지 입력 가능합니다`,
                  }
                : {
                    max: VALIDATION_RULES.PROJECT_NAME_MAX_LENGTH,
                    message: VALIDATION_MESSAGES.PROJECT_NAME_MAX_LENGTH,
                  },
            ]}
            validateFirst>
            <Input
              placeholder={`${experimentId ? '실험명' : '프로젝트명'}을 입력하세요`}
              className={styles.nameInput}
              maxLength={
                experimentId ? VALIDATION_RULES.EXPERIMENT_NAME_MAX_LENGTH : VALIDATION_RULES.PROJECT_NAME_MAX_LENGTH
              }
              allowClear
            />
          </Form.Item>
          <Form.Item name="description" label="설명" required>
            <DescriptionTextArea
              placeholder="설명을 입력하세요"
              showHelper
              minLength={VALIDATION_RULES.DESCRIPTION_MIN_LENGTH}
              maxLength={VALIDATION_RULES.DESCRIPTION_MAX_LENGTH}
              status={editValidation.descriptionError ? 'error' : undefined}
            />
          </Form.Item>
        </Form>
      </Modal>

      {isPreparingWorkbench && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <Spin
              size="large"
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin className={styles.spinnerIcon} />}
            />
            <div className={styles.loadingText}>워크벤치 준비중...</div>
          </div>
        </div>
      )}
    </article>
  );
};
